import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Plus, Trash2, ArrowRight, HelpCircle, ChevronDown } from "lucide-react";

import { toast } from '../../utils/toast.js';
const orgTypes = ["Non-Profit Organization","Charity Foundation","Public Association","International NGO","Social Enterprise","Community Center","Youth Organization","Educational Institution","Healthcare Organization","Environmental NGO","Human Rights Organization","Cultural Foundation","Other"];
// Values (used as form data / submitted to the backend) stay fixed; display labels are
// looked up via t("authPages.orgReg.ageGroups.<value>") / t("authPages.orgReg.orgNameLanguages.<value>")
// in the component below so they render in the active UI language instead of always English.
const ageGroupValues = ["all","0-12","13-17","18-25","26-35","36-50","51-65","65+"];
const focusAreasList = ["Education & Literacy","Healthcare & Medicine","Environmental Protection","Human Rights","Women Empowerment","Children & Youth Development","Poverty Alleviation","Disaster Relief","Community Development","Arts & Culture","Sports & Recreation","Technology & Innovation","Animal Welfare","Mental Health","Elderly Care","Refugee Support","Other"];
const legalForms = ["Non-Governmental Organization (NGO)","Public Organization","Foundation","Charity Organization","Association","Social Enterprise","Community-Based Organization","International Organization","Religious Organization","Professional Association","Other"];
const languageValues = ["Armenian","English","Russian","French","German","Spanish","Other"];

// Persisted on every keystroke (not just on submit) so a reload or a back/forward
// navigation while still on this step doesn't lose what's been typed — see the
// matching load/save effects below.
const DRAFT_KEY = "orgOnboardingDraftStepOne";
const defaultFormData = {
  nameBlocks: [{ language: "Armenian", name: "" }],
  orgType: [], customOrgType: "",
  audiences: [],
  description: "", mission: "",
  focusAreas: [], customFocusArea: "",
  establishedYear: "",
  legalForm: [], customLegalForm: ""
};

function Reg() {
  const { t } = useTranslation();
  const ageGroups = ageGroupValues.map(value => ({ value, label: t(`authPages.orgReg.ageGroups.${value}`) }));
  const [formData, setFormData] = useState(() => {
    try {
      const saved = sessionStorage.getItem(DRAFT_KEY);
      return saved ? { ...defaultFormData, ...JSON.parse(saved) } : defaultFormData;
    } catch {
      return defaultFormData;
    }
  });

  const [showFocusDropdown, setShowFocusDropdown] = useState(false);
  const [showLegalDropdown, setShowLegalDropdown] = useState(false);
  const [showOrgTypeDropdown, setShowOrgTypeDropdown] = useState(false);
  const [isMobile, setIsMobile] = useState(typeof window !== "undefined" ? window.innerWidth <= 768 : false);

  const navigate = useNavigate();
  const focusDropdownRef = useRef(null);
  const legalDropdownRef = useRef(null);
  const orgTypeDropdownRef = useRef(null);

  useEffect(() => {
    const fn = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", fn);
    return () => window.removeEventListener("resize", fn);
  }, []);

  useEffect(() => {
    sessionStorage.setItem(DRAFT_KEY, JSON.stringify(formData));
  }, [formData]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (focusDropdownRef.current && !focusDropdownRef.current.contains(e.target)) setShowFocusDropdown(false);
      if (legalDropdownRef.current && !legalDropdownRef.current.contains(e.target)) setShowLegalDropdown(false);
      if (orgTypeDropdownRef.current && !orgTypeDropdownRef.current.contains(e.target)) setShowOrgTypeDropdown(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const updateNameBlock = (index, field, value) => {
    if (field === "name" && value.length > 100) return;
    setFormData(prev => { const next = [...prev.nameBlocks]; next[index] = { ...next[index], [field]: value }; return { ...prev, nameBlocks: next }; });
  };
  const addNameBlock = () => setFormData(prev => ({ ...prev, nameBlocks: [...prev.nameBlocks, { language: "Armenian", name: "" }] }));
  const removeNameBlock = (index) => { if (formData.nameBlocks.length > 1) setFormData(prev => ({ ...prev, nameBlocks: prev.nameBlocks.filter((_, i) => i !== index) })); };

  const toggleAudience = (ag) => {
    if (ag === "all") setFormData(prev => ({ ...prev, audiences: prev.audiences.includes("all") ? [] : ["all"] }));
    else setFormData(prev => ({ ...prev, audiences: prev.audiences.includes(ag) ? prev.audiences.filter(a => a !== ag && a !== "all") : [...prev.audiences.filter(a => a !== "all"), ag] }));
  };

  const toggleOrgType = (type) => setFormData(prev => ({ ...prev, orgType: prev.orgType.includes(type) ? prev.orgType.filter(t => t !== type) : [...prev.orgType, type] }));
  const addCustomOrgType = () => { const t = formData.customOrgType.trim(); if (t && !formData.orgType.includes(t)) setFormData(prev => ({ ...prev, orgType: [...prev.orgType, t], customOrgType: "" })); };
  const removeOrgType = (type) => setFormData(prev => ({ ...prev, orgType: prev.orgType.filter(t => t !== type) }));

  const toggleFocusArea = (area) => setFormData(prev => ({ ...prev, focusAreas: prev.focusAreas.includes(area) ? prev.focusAreas.filter(a => a !== area) : [...prev.focusAreas, area] }));
  const addCustomFocusArea = () => { const t = formData.customFocusArea.trim(); if (t && !formData.focusAreas.includes(t)) setFormData(prev => ({ ...prev, focusAreas: [...prev.focusAreas, t], customFocusArea: "" })); };
  const removeFocusArea = (area) => setFormData(prev => ({ ...prev, focusAreas: prev.focusAreas.filter(a => a !== area) }));

  const toggleLegalForm = (form) => setFormData(prev => ({ ...prev, legalForm: prev.legalForm.includes(form) ? prev.legalForm.filter(f => f !== form) : [...prev.legalForm, form] }));
  const addCustomLegalForm = () => { const t = formData.customLegalForm.trim(); if (t && !formData.legalForm.includes(t)) setFormData(prev => ({ ...prev, legalForm: [...prev.legalForm, t], customLegalForm: "" })); };
  const removeLegalForm = (form) => setFormData(prev => ({ ...prev, legalForm: prev.legalForm.filter(f => f !== form) }));

  const handleDescriptionChange = (e) => { if (e.target.value.length <= 500) setFormData(prev => ({ ...prev, description: e.target.value })); };
  const handleMissionChange = (e) => { if (e.target.value.length <= 300) setFormData(prev => ({ ...prev, mission: e.target.value })); };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.nameBlocks[0].name.trim()) { toast(t("authPages.orgReg.alertName")); return; }
    if (formData.orgType.length === 0) { toast(t("authPages.orgReg.alertOrgType")); return; }
    if (formData.audiences.length === 0) { toast(t("authPages.orgReg.alertAudience")); return; }
    if (formData.focusAreas.length === 0) { toast(t("authPages.orgReg.alertFocusArea")); return; }
    if (!formData.description.trim()) { toast(t("authPages.orgReg.alertDescription")); return; }
    if (!formData.mission.trim()) { toast(t("authPages.orgReg.alertMission")); return; }
    if (!formData.establishedYear) { toast(t("authPages.orgReg.alertYear")); return; }
    if (formData.legalForm.length === 0) { toast(t("authPages.orgReg.alertLegalForm")); return; }
    sessionStorage.setItem("orgOnboardingStepOne", JSON.stringify(formData));
    navigate("/regnext");
  };

  const getFocusAreasDisplay = () => formData.focusAreas.length === 0 ? t("authPages.orgReg.selectFocusAreas") : formData.focusAreas.length === 1 ? formData.focusAreas[0] : t("authPages.orgReg.areasSelected", { count: formData.focusAreas.length });
  const getLegalFormDisplay = () => formData.legalForm.length === 0 ? t("authPages.orgReg.selectLegalForm") : formData.legalForm.length === 1 ? formData.legalForm[0] : t("authPages.orgReg.formsSelected", { count: formData.legalForm.length });
  const getOrgTypeDisplay = () => formData.orgType.length === 0 ? t("authPages.orgReg.selectOrgType") : formData.orgType.length === 1 ? formData.orgType[0] : t("authPages.orgReg.typesSelected", { count: formData.orgType.length });

  // ── Shared styles ──
  const inputStyle = { width: "100%", padding: isMobile ? "10px 12px" : "11px 14px", borderRadius: "10px", border: "1.5px solid #ddd", fontSize: isMobile ? "13px" : "14px", outline: "none", boxSizing: "border-box", fontFamily: "inherit", transition: "border-color 0.2s", background: "white" };
  const textareaStyle = { ...inputStyle, resize: "none", lineHeight: 1.5 };
  const labelStyle = { display: "flex", flexDirection: "column", gap: "5px", fontSize: isMobile ? "12px" : "13px", fontWeight: 600, color: "#444" };
  const sectionStyle = { background: "white", borderRadius: "14px", padding: isMobile ? "14px 12px" : "20px", border: "1px solid #e9ecef", display: "flex", flexDirection: "column", gap: isMobile ? "10px" : "14px" };
  const sectionTitleStyle = { fontSize: isMobile ? "13px" : "15px", fontWeight: 700, color: "#1a3d2e", margin: 0 };
  const dropdownTriggerStyle = { width: "100%", padding: isMobile ? "10px 12px" : "11px 14px", borderRadius: "10px", border: "1.5px solid #ddd", background: "white", fontSize: isMobile ? "13px" : "14px", fontFamily: "inherit", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "space-between", color: "#444", boxSizing: "border-box" };
  const dropdownMenuStyle = { position: "absolute", top: "calc(100% + 4px)", left: 0, right: 0, background: "white", border: "1.5px solid #ddd", borderRadius: "10px", boxShadow: "0 8px 24px rgba(0,0,0,0.12)", zIndex: 100, maxHeight: isMobile ? "220px" : "260px", overflowY: "auto" };
  const chipStyle = { display: "inline-flex", alignItems: "center", gap: "6px", background: "#e8f5e9", color: "#1a3d2e", padding: "3px 8px 3px 10px", borderRadius: "999px", fontSize: isMobile ? "11px" : "12px", fontWeight: 600 };
  const ghostBtnStyle = { background: "none", border: "1.5px dashed #32614d", borderRadius: "8px", color: "#32614d", padding: isMobile ? "8px 12px" : "9px 16px", fontSize: isMobile ? "12px" : "13px", fontWeight: 600, cursor: "pointer", display: "flex", alignItems: "center", gap: "6px", fontFamily: "inherit", width: "100%" };

  const DropdownMenu = ({ items, selected, onToggle }) => (
    <div style={dropdownMenuStyle}>
      {items.map(item => (
        <label key={item} style={{ display: "flex", alignItems: "center", gap: "10px", padding: isMobile ? "9px 12px" : "10px 14px", cursor: "pointer", borderBottom: "1px solid #f5f5f5", fontSize: isMobile ? "12px" : "13px", color: "#333" }}>
          <input type="checkbox" checked={selected.includes(item)} onChange={() => onToggle(item)} style={{ width: "auto", flexShrink: 0, accentColor: "#32614d" }} />
          {item}
        </label>
      ))}
    </div>
  );

  const ChipList = ({ items, onRemove }) => (
    <div style={{ display: "flex", flexWrap: "wrap", gap: "6px", marginTop: "6px" }}>
      {items.map(item => (
        <span key={item} style={chipStyle}>
          {item}
          <button type="button" onClick={() => onRemove(item)} style={{ background: "none", border: "none", cursor: "pointer", padding: 0, color: "#1a3d2e", display: "flex", lineHeight: 1 }}>
            <Trash2 size={12} />
          </button>
        </span>
      ))}
    </div>
  );

  const CustomInput = ({ value, onChange, placeholder, onAdd }) => (
    <div style={{ display: "flex", gap: "8px", marginTop: "8px" }}>
      <input type="text" value={value} onChange={onChange} placeholder={placeholder} maxLength={50}
        style={{ ...inputStyle, flex: 1 }}
        onFocus={e => e.target.style.borderColor = "#32614d"} onBlur={e => e.target.style.borderColor = "#ddd"} />
      <button type="button" onClick={onAdd}
        style={{ padding: "0 14px", background: "#32614d", border: "none", borderRadius: "10px", color: "white", cursor: "pointer", display: "flex", alignItems: "center" }}>
        <Plus size={16} />
      </button>
    </div>
  );

  return (
    <div style={{ background: "#f5f7f0", minHeight: "100vh" }}>
      {/* Progress header */}
      <div style={{ background: "white", borderBottom: "1px solid #e9ecef", padding: isMobile ? "12px 14px" : "16px 24px", position: "sticky", top: 0, zIndex: 50 }}>
        <div style={{ maxWidth: 760, margin: "0 auto", display: "flex", gap: isMobile ? "6px" : "0", alignItems: "center", justifyContent: "space-between" }}>
          {[
            { n: 1, label: isMobile ? t("authPages.orgReg.stepProfileShort") : t("authPages.orgReg.stepProfileLong") },
            { n: 2, label: isMobile ? t("authPages.orgReg.stepContactShort") : t("authPages.orgReg.stepContactLong") },
            { n: 3, label: isMobile ? t("authPages.orgReg.stepTeamShort") : t("authPages.orgReg.stepTeamLong") },
            { n: 4, label: t("authPages.orgReg.stepVerification") },
          ].map((step, i) => (
            <React.Fragment key={step.n}>
              <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "4px", flex: 1 }}>
                <div style={{ width: isMobile ? 28 : 32, height: isMobile ? 28 : 32, borderRadius: "50%", background: step.n === 1 ? "#32614d" : "#e9ecef", color: step.n === 1 ? "white" : "#999", display: "flex", alignItems: "center", justifyContent: "center", fontSize: isMobile ? "12px" : "13px", fontWeight: 700 }}>
                  {step.n}
                </div>
                <span style={{ fontSize: isMobile ? "9px" : "11px", color: step.n === 1 ? "#32614d" : "#999", fontWeight: step.n === 1 ? 700 : 400, textAlign: "center", whiteSpace: "nowrap" }}>{step.label}</span>
              </div>
              {i < 3 && <div style={{ height: "2px", flex: 2, background: "#e9ecef", marginBottom: isMobile ? "16px" : "20px" }} />}
            </React.Fragment>
          ))}
        </div>
      </div>

      {/* Form */}
      <div style={{ maxWidth: 760, margin: "0 auto", padding: isMobile ? "16px 14px 100px" : "28px 20px 60px" }}>
        <h2 style={{ fontSize: isMobile ? "16px" : "20px", fontWeight: 700, color: "#1a3d2e", margin: "0 0 16px" }}>{t("authPages.orgReg.stepProfileLong")}</h2>

        <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: isMobile ? "12px" : "16px" }}>

          {/* Organization Name */}
          <section style={sectionStyle}>
            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <h3 style={sectionTitleStyle}>{t("authPages.orgReg.orgNameTitle")}</h3>
              <div style={{ position: "relative", cursor: "help" }} title={t("authPages.orgReg.orgNameTooltip")}>
                <HelpCircle size={14} color="#aaa" />
              </div>
            </div>
            <p style={{ margin: 0, fontSize: isMobile ? "11px" : "12px", color: "#888" }}>{t("authPages.orgReg.orgNameDesc")}</p>

            {formData.nameBlocks.map((block, index) => (
              <div key={index} style={{ display: "grid", gridTemplateColumns: isMobile ? "100px 1fr" : "140px 1fr", gap: "10px", alignItems: "flex-end" }}>
                <label style={labelStyle}>
                  <span>{t("authPages.orgReg.language")} {index === 0 && <span style={{ color: "#e53935" }}>*</span>}</span>
                  <select value={block.language} onChange={e => updateNameBlock(index, "language", e.target.value)}
                    style={{ ...inputStyle, appearance: "auto" }}>
                    {languageValues.map(l => <option key={l} value={l}>{t(`authPages.orgReg.orgNameLanguages.${l}`)}</option>)}
                  </select>
                </label>
                <label style={labelStyle}>
                  <span>{t("authPages.orgReg.orgNameLabel")} {index === 0 && <span style={{ color: "#e53935" }}>*</span>}</span>
                  <div style={{ display: "flex", gap: "8px" }}>
                    <div style={{ flex: 1, position: "relative" }}>
                      <input type="text" value={block.name} onChange={e => updateNameBlock(index, "name", e.target.value)}
                        placeholder={t("authPages.orgReg.orgNamePlaceholder")} required={index === 0} maxLength={50} style={inputStyle}
                        onFocus={e => e.target.style.borderColor = "#32614d"} onBlur={e => e.target.style.borderColor = "#ddd"} />
                      <span style={{ position: "absolute", right: 8, bottom: 6, fontSize: "9px", color: "#bbb" }}>{block.name.length}/50</span>
                    </div>
                    {formData.nameBlocks.length > 1 && (
                      <button type="button" onClick={() => removeNameBlock(index)}
                        style={{ padding: "0 10px", background: "#ffebee", border: "none", borderRadius: "8px", cursor: "pointer", color: "#e53935", display: "flex", alignItems: "center" }}>
                        <Trash2 size={15} />
                      </button>
                    )}
                  </div>
                </label>
              </div>
            ))}
            <button type="button" style={ghostBtnStyle} onClick={addNameBlock}>
              <Plus size={15} /> {t("authPages.orgReg.addNameInAnotherLanguage")}
            </button>
          </section>

          {/* Organization Type */}
          <section style={sectionStyle}>
            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <h3 style={sectionTitleStyle}>{t("authPages.orgReg.orgTypeTitle")} <span style={{ color: "#e53935" }}>*</span></h3>
            </div>
            <div ref={orgTypeDropdownRef} style={{ position: "relative" }}>
              <button type="button" style={dropdownTriggerStyle} onClick={() => { setShowOrgTypeDropdown(p => !p); setShowFocusDropdown(false); setShowLegalDropdown(false); }}>
                <span style={{ color: formData.orgType.length ? "#333" : "#aaa" }}>{getOrgTypeDisplay()}</span>
                <ChevronDown size={15} style={{ transform: showOrgTypeDropdown ? "rotate(180deg)" : "none", transition: "0.2s" }} />
              </button>
              {showOrgTypeDropdown && <DropdownMenu items={orgTypes} selected={formData.orgType} onToggle={toggleOrgType} />}
            </div>
            {formData.orgType.length > 0 && <ChipList items={formData.orgType} onRemove={removeOrgType} />}
            {formData.orgType.includes("Other") && (
              <CustomInput value={formData.customOrgType} onChange={e => setFormData(p => ({ ...p, customOrgType: e.target.value }))} placeholder={t("authPages.orgReg.specifyOrgType")} onAdd={addCustomOrgType} />
            )}
          </section>

          {/* Target Age Groups */}
          <section style={sectionStyle}>
            <h3 style={sectionTitleStyle}>{t("authPages.orgReg.ageGroupsTitle")} <span style={{ color: "#e53935" }}>*</span></h3>
            <div style={{ display: "grid", gridTemplateColumns: isMobile ? "repeat(2,1fr)" : "repeat(4,1fr)", gap: "8px" }}>
              {ageGroups.map(g => {
                const active = formData.audiences.includes(g.value);
                return (
                  <label key={g.value}
                    style={{ padding: isMobile ? "8px 6px" : "10px 8px", borderRadius: "10px", border: `1.5px solid ${active ? "#32614d" : "#e9ecef"}`, background: active ? "#e8f5e9" : "white", cursor: "pointer", display: "flex", flexDirection: "column", alignItems: "center", gap: "2px", transition: "all 0.18s" }}>
                    {/* Toggling belongs on the checkbox's onChange, not a separate label onClick — a
                        label wrapping its control fires a second, synthetic click on that control,
                        so an onClick here as well as an onChange on the input below double-fires
                        and cancels the toggle back out on every click. */}
                    <input type="checkbox" checked={active} onChange={() => toggleAudience(g.value)} style={{ display: "none" }} />
                    <span style={{ fontSize: isMobile ? "11px" : "12px", fontWeight: 700, color: active ? "#1a3d2e" : "#555", textAlign: "center" }}>{g.label.split("(")[0].trim()}</span>
                    {g.label.includes("(") && <span style={{ fontSize: "10px", color: active ? "#32614d" : "#aaa" }}>({g.label.split("(")[1].replace(")", "")})</span>}
                  </label>
                );
              })}
            </div>
            {formData.audiences.length > 0 && (
              <p style={{ fontSize: "11px", color: "#32614d", margin: 0, fontWeight: 600 }}>
                ✓ {formData.audiences.map(a => ageGroups.find(g => g.value === a)?.label).join(", ")}
              </p>
            )}
          </section>

          {/* Focus Areas */}
          <section style={sectionStyle}>
            <h3 style={sectionTitleStyle}>{t("authPages.orgReg.focusAreasTitle")} <span style={{ color: "#e53935" }}>*</span></h3>
            <div ref={focusDropdownRef} style={{ position: "relative" }}>
              <button type="button" style={dropdownTriggerStyle} onClick={() => { setShowFocusDropdown(p => !p); setShowOrgTypeDropdown(false); setShowLegalDropdown(false); }}>
                <span style={{ color: formData.focusAreas.length ? "#333" : "#aaa" }}>{getFocusAreasDisplay()}</span>
                <ChevronDown size={15} style={{ transform: showFocusDropdown ? "rotate(180deg)" : "none", transition: "0.2s" }} />
              </button>
              {showFocusDropdown && <DropdownMenu items={focusAreasList} selected={formData.focusAreas} onToggle={toggleFocusArea} />}
            </div>
            {formData.focusAreas.length > 0 && <ChipList items={formData.focusAreas} onRemove={removeFocusArea} />}
            {formData.focusAreas.includes("Other") && (
              <CustomInput value={formData.customFocusArea} onChange={e => setFormData(p => ({ ...p, customFocusArea: e.target.value }))} placeholder={t("authPages.orgReg.specifyFocusArea")} onAdd={addCustomFocusArea} />
            )}
          </section>

          {/* Organization Details */}
          <section style={sectionStyle}>
            <h3 style={sectionTitleStyle}>{t("authPages.orgReg.detailsTitle")}</h3>

            <label style={labelStyle}>
              <span>{t("authPages.orgReg.briefDescription")} <span style={{ color: "#e53935" }}>*</span> <span style={{ fontSize: "11px", color: "#aaa", fontWeight: 400 }}>({formData.description.length}/500)</span></span>
              <textarea rows={isMobile ? 3 : 4} value={formData.description} onChange={handleDescriptionChange}
                placeholder={t("authPages.orgReg.briefDescriptionPlaceholder")} maxLength={500}
                required style={textareaStyle}
                onFocus={e => e.target.style.borderColor = "#32614d"} onBlur={e => e.target.style.borderColor = "#ddd"} />
            </label>

            <label style={labelStyle}>
              <span>{t("authPages.orgReg.missionStatement")} <span style={{ color: "#e53935" }}>*</span> <span style={{ fontSize: "11px", color: "#aaa", fontWeight: 400 }}>({formData.mission.length}/300)</span></span>
              <textarea rows={isMobile ? 3 : 4} value={formData.mission} onChange={handleMissionChange}
                placeholder={t("authPages.orgReg.missionPlaceholder")} maxLength={300}
                required style={textareaStyle}
                onFocus={e => e.target.style.borderColor = "#32614d"} onBlur={e => e.target.style.borderColor = "#ddd"} />
            </label>

            <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: "12px" }}>
              <label style={labelStyle}>
                <span>{t("authPages.orgReg.yearEstablished")} <span style={{ color: "#e53935" }}>*</span></span>
                <input type="number" min="1900" max={new Date().getFullYear()} value={formData.establishedYear}
                  onChange={e => setFormData(p => ({ ...p, establishedYear: e.target.value }))}
                  placeholder="YYYY" required style={inputStyle}
                  onFocus={e => e.target.style.borderColor = "#32614d"} onBlur={e => e.target.style.borderColor = "#ddd"} />
              </label>

              <label style={labelStyle}>
                <span>{t("authPages.orgReg.legalForm")} <span style={{ color: "#e53935" }}>*</span></span>
                <div ref={legalDropdownRef} style={{ position: "relative" }}>
                  <button type="button" style={dropdownTriggerStyle} onClick={() => { setShowLegalDropdown(p => !p); setShowOrgTypeDropdown(false); setShowFocusDropdown(false); }}>
                    <span style={{ color: formData.legalForm.length ? "#333" : "#aaa", fontSize: isMobile ? "13px" : "14px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flex: 1, textAlign: "left" }}>{getLegalFormDisplay()}</span>
                    <ChevronDown size={15} style={{ transform: showLegalDropdown ? "rotate(180deg)" : "none", transition: "0.2s", flexShrink: 0 }} />
                  </button>
                  {showLegalDropdown && <DropdownMenu items={legalForms} selected={formData.legalForm} onToggle={toggleLegalForm} />}
                </div>
                {formData.legalForm.length > 0 && <ChipList items={formData.legalForm} onRemove={removeLegalForm} />}
                {formData.legalForm.includes("Other") && (
                  <CustomInput value={formData.customLegalForm} onChange={e => setFormData(p => ({ ...p, customLegalForm: e.target.value }))} placeholder={t("authPages.orgReg.specifyLegalForm")} onAdd={addCustomLegalForm} />
                )}
              </label>
            </div>
          </section>

          {/* Submit */}
          <button type="submit"
            style={{ padding: isMobile ? "13px 20px" : "14px 28px", background: "linear-gradient(135deg,#32614d,#2a5240)", color: "white", border: "none", borderRadius: "12px", fontSize: isMobile ? "14px" : "15px", fontWeight: 700, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: "8px", fontFamily: "inherit" }}>
            {t("authPages.orgReg.continueStep2")} <ArrowRight size={18} />
          </button>
        </form>
      </div>
    </div>
  );
}

export default Reg;